import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GSDocumentToolNavbar } from '@/components/layout/GSDocumentToolNavbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useGSProduct } from '@/hooks/useGSProduct';
import { GSProductDocumentation } from '@/components/gs-document-tool/GSProductDocumentation';

export default function GSProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const { product, isLoading, error } = useGSProduct(productId || '');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
      <GSDocumentToolNavbar />
        <main className="container py-12">
          <div className="flex justify-center items-center h-64">
            <p>Loading product details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <GSDocumentToolNavbar />
        <main className="container py-12">
          <div className="bg-destructive/10 p-4 rounded-md text-destructive mb-6">
            Product not found or you don't have access to view it.
          </div>
          <Link to="/gs-document-tool">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to GS Document Tool
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GSDocumentToolNavbar />
      
      <main className="container py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/gs-document-tool">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">{product.name}</h1>
              <p className="text-muted-foreground">
                {product.product_type} • {product.certification_status}
              </p>
            </div>
          </div>
        </div>

        {/* Product Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Product Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Name</label>
                <p className="text-sm">{product.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Type</label>
                <p className="text-sm">{product.product_type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Certification Status</label>
                <p className="text-sm">{product.certification_status}</p>
              </div>
              {product.manufacturer && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Manufacturer</label>
                  <p className="text-sm">{product.manufacturer}</p>
                </div>
              )}
              {product.description && (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="text-sm">{product.description}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documentation Requirements */}
        <GSProductDocumentation productId={product.id} />
      </main>
    </div>
  );
}